import { c as _c } from "react/compiler-runtime";
import { cx } from 'class-variance-authority';
import { useId } from 'react';
import { type FieldValues, type Path, type RegisterOptions, useFormContext, useFormState } from 'react-hook-form';
import type { LabelTooltipProps } from '../../label';
import Radio, { type RadioProps } from '../../radio';
import FormWrapper from '../internals/form-wrapper';
export type FormRadioOption = {
  label: string | React.ReactNode;
  value: string;
  hint?: string;
};
export type FormRadioOptions = FormRadioOption[];
export type FormRadioGroupProps<TFieldValues extends FieldValues> = {
  name: Path<TFieldValues>;
  options: FormRadioOptions;
  rules?: RegisterOptions<TFieldValues, Path<TFieldValues>>;
  errorPosition?: 'absolute' | 'relative';
  orientation?: 'vertical' | 'horizontal';
  addon?: React.ReactNode;
  tooltip?: LabelTooltipProps;
  label?: string | React.ReactNode;
  className?: string;
  hideError?: boolean;
} & Omit<RadioProps, 'checked' | 'defaultChecked' | 'name' | 'onBlur' | 'ref' | 'hasError' | 'label' | 'value' | 'hint'>;
export default function FormRadioGroup(t0) {
  const $ = _c(58);
  let addon;
  let baseId;
  let className;
  let label;
  let name;
  let onChange;
  let options;
  let radioProps;
  let rules;
  let t1;
  let t2;
  let t3;
  let tooltip;
  if ($[0] !== t0) {
    ({
      label,
      name,
      rules,
      options,
      id: baseId,
      errorPosition: t1,
      orientation: t2,
      addon,
      onChange,
      tooltip,
      className,
      hideError: t3,
      ...radioProps
    } = t0);
    $[0] = t0;
    $[1] = addon;
    $[2] = baseId;
    $[3] = className;
    $[4] = label;
    $[5] = name;
    $[6] = onChange;
    $[7] = options;
    $[8] = radioProps;
    $[9] = rules;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
    $[13] = tooltip;
  } else {
    addon = $[1];
    baseId = $[2];
    className = $[3];
    label = $[4];
    name = $[5];
    onChange = $[6];
    options = $[7];
    radioProps = $[8];
    rules = $[9];
    t1 = $[10];
    t2 = $[11];
    t3 = $[12];
    tooltip = $[13];
  }
  const errorPosition = t1 === undefined ? "relative" : t1;
  const orientation = t2 === undefined ? "vertical" : t2;
  const hideError = t3 === undefined ? false : t3;
  const {
    register,
    control,
    getFieldState
  } = useFormContext();
  let t4;
  if ($[14] !== control || $[15] !== name || $[16] !== radioProps.disabled) {
    t4 = {
      control,
      name,
      disabled: radioProps.disabled
    };
    $[14] = control;
    $[15] = name;
    $[16] = radioProps.disabled;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const formState = useFormState(t4);
  let t5;
  if ($[18] !== formState || $[19] !== getFieldState || $[20] !== name) {
    t5 = getFieldState(name, formState);
    $[18] = formState;
    $[19] = getFieldState;
    $[20] = name;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  const {
    error
  } = t5;
  const contextId = useId();
  const id = baseId || contextId;
  const t6 = error?.message;
  const t7 = formState.disabled || radioProps.disabled;
  const t8 = orientation === "vertical" ? "flex-col gap-3" : "flex-row gap-4";
  let t9;
  if ($[22] !== className || $[23] !== t8) {
    t9 = cx("flex", t8, className);
    $[22] = className;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== error || $[26] !== formState.disabled || $[27] !== id || $[28] !== name || $[29] !== onChange || $[30] !== options || $[31] !== radioProps || $[32] !== register || $[33] !== rules) {
    let t11;
    if ($[35] !== error || $[36] !== formState.disabled || $[37] !== id || $[38] !== name || $[39] !== onChange || $[40] !== radioProps || $[41] !== register || $[42] !== rules) {
      t11 = t12 => {
        const {
          label: label_0,
          value,
          hint
        } = t12;
        const {
          onChange: reactHookFormOnChange,
          ...rest
        } = register(name, {
          ...rules
        });
        return <Radio key={value} {...radioProps} {...rest} onChange={e => {
          reactHookFormOnChange(e);
          onChange?.(e);
        }} disabled={formState.disabled || radioProps.disabled} hasError={!!error} id={`${id}-${value}`} data-dd-privacy="mask" hint={hint} label={label_0} value={value} />;
      };
      $[35] = error;
      $[36] = formState.disabled;
      $[37] = id;
      $[38] = name;
      $[39] = onChange;
      $[40] = radioProps;
      $[41] = register;
      $[42] = rules;
      $[43] = t11;
    } else {
      t11 = $[43];
    }
    t10 = options.map(t11);
    $[25] = error;
    $[26] = formState.disabled;
    $[27] = id;
    $[28] = name;
    $[29] = onChange;
    $[30] = options;
    $[31] = radioProps;
    $[32] = register;
    $[33] = rules;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[44] !== t10 || $[45] !== t9) {
    t11 = <div className={t9}>{t10}</div>;
    $[44] = t10;
    $[45] = t9;
    $[46] = t11;
  } else {
    t11 = $[46];
  }
  let t12;
  if ($[47] !== addon || $[48] !== errorPosition || $[49] !== hideError || $[50] !== id || $[51] !== label || $[52] !== orientation || $[53] !== t11 || $[54] !== t6 || $[55] !== t7 || $[56] !== tooltip) {
    t12 = <FormWrapper id={id} label={label} error={t6} errorPosition={errorPosition} addon={addon} size="default" orientation={orientation} fullWidth={true} disabled={t7} tooltip={tooltip} hideError={hideError}>{t11}</FormWrapper>;
    $[47] = addon;
    $[48] = errorPosition;
    $[49] = hideError;
    $[50] = id;
    $[51] = label;
    $[52] = orientation;
    $[53] = t11;
    $[54] = t6;
    $[55] = t7;
    $[56] = tooltip;
    $[57] = t12;
  } else {
    t12 = $[57];
  }
  return t12;
}